/**
 * Хранилище access-токена.
 *
 * §10.1 требует держать токен в памяти, а не в localStorage: содержимое
 * localStorage доступно любому скрипту на странице, и при XSS токен утекает
 * вместе с ним. Переживать перезагрузку страницы должен refresh-токен
 * в httpOnly-cookie, а не access-токен.
 *
 * Это не Zustand-хранилище намеренно: к токену обращается функция запроса,
 * которая не является React-компонентом и хуками пользоваться не может.
 */

let accessToken: string | null = null;
const listeners = new Set<(token: string | null) => void>();

/**
 * Отметка «вход был» — единственное, что переживает перезагрузку.
 *
 * Сам токен по-прежнему живёт только в памяти. Здесь лежит признак, а не
 * секрет: по нему приложение решает, есть ли смысл спрашивать сервер
 * о восстановлении сессии. Без него каждый первый заход на экран входа
 * стучится в `/auth/refresh` заведомо впустую и оставляет в консоли
 * красный 401, за которым нет никакой ошибки.
 */
const SESSION_FLAG = 'netdeck.session';

function readFlag(): boolean {
  try {
    return localStorage.getItem(SESSION_FLAG) === '1';
  } catch {
    // Приватный режим и запрет хранилища: считаем, что сессия могла быть,
    // и спрашиваем сервер. Лишний запрос лучше потерянной сессии.
    return true;
  }
}

function writeFlag(present: boolean): void {
  try {
    if (present) {
      localStorage.setItem(SESSION_FLAG, '1');
    } else {
      localStorage.removeItem(SESSION_FLAG);
    }
  } catch {
    // Не записалось — ничего страшного: приложение спросит сервер.
  }
}

export const authStore = {
  get token(): string | null {
    return accessToken;
  },

  set(token: string | null): void {
    accessToken = token;

    if (token !== null) {
      writeFlag(true);
    }

    for (const listener of listeners) {
      listener(token);
    }
  },

  clear(): void {
    writeFlag(false);
    authStore.set(null);
  },

  /**
   * Стоит ли спрашивать сервер о сессии.
   *
   * `false` — входа в этом браузере не было или из него вышли: refresh-токена
   * нет, и запрос вернёт 401. Спрашивать всё равно значит гарантированно
   * получить отказ и показать его в консоли как ошибку.
   */
  get mayHaveSession(): boolean {
    return readFlag();
  },

  /** Подписка на смену токена: по ней интерфейс переходит к экрану входа. */
  subscribe(listener: (token: string | null) => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};
